import { useState, useEffect, useRef } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { X, Sparkles, RefreshCw } from 'lucide-react';
import MultiSelectFilter from '@/components/MultiSelectFilter';
import ProjectAutocomplete from '@/components/ProjectAutocomplete';

function FilterBar({ projects, categories, users, statuses, onFilterChange, onAIAnalysis, onRefresh, isRefreshing, initialFilters }) {
  // Default empty filters
  const defaultFilters = {
    project: '',
    category: '',
    user: '',
    status: '',
    includeResolved: false,
  };
  
  // Initialize with initialFilters or defaults
  const [filters, setFilters] = useState(initialFilters || defaultFilters);
  const isFirstMount = useRef(true);

  // Sync filters with initialFilters prop on mount only
  useEffect(() => {
    if (isFirstMount.current && initialFilters) {
      setFilters(initialFilters);
      isFirstMount.current = false;
    }
  }, [initialFilters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClearFilters = () => {
    const emptyFilters = { project: '', category: '', user: '', status: '', includeResolved: false };
    setFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'includeResolved') return false; // Don't count includeResolved as active filter
    return value !== '' && value !== 'all';
  });

  return (
    <div
      className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-4 rounded-md backdrop-blur-sm mb-6"
      data-testid="filter-bar"
    >
      <div className="flex items-center gap-4 flex-wrap">
        <span className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono">
          Filtri:
        </span>

        {/* Multi-select for Status */}
        <MultiSelectFilter
          options={statuses || []}
          value={filters.status}
          onChange={(value) => handleFilterChange('status', value)}
          placeholder="Stato"
          testId="filter-status"
        />

        {/* Autocomplete for Project */}
        {projects && projects.length > 0 && (
          <ProjectAutocomplete
            projects={projects}
            value={filters.project}
            onChange={(value) => handleFilterChange('project', value)}
            placeholder="Progetto"
            allowAll={true}
            className="w-[220px]"
          />
        )}

        {/* Multi-select for Category */}
        {categories && categories.length > 0 && (
          <MultiSelectFilter
            options={categories}
            value={filters.category}
            onChange={(value) => handleFilterChange('category', value)}
            placeholder="Categoria"
            testId="filter-category"
          />
        )}

        {/* Single select for User */}
        {users && users.length > 0 && (
          <Select value={filters.user || 'all'} onValueChange={(value) => handleFilterChange('user', value === 'all' ? '' : value)}>
            <SelectTrigger
              className="w-[180px] bg-black/20 border-[rgb(39,39,42)] font-mono text-sm"
              data-testid="filter-user"
            >
              <SelectValue placeholder="Utente" />
            </SelectTrigger>
            <SelectContent className="bg-[rgb(24,24,27)] border-[rgb(39,39,42)]">
              <SelectItem value="all" className="font-mono font-bold">
                -- Tutti --
              </SelectItem>
              {users.map((user) => (
                <SelectItem key={user} value={user} className="font-mono">
                  {user}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Checkbox for Include Resolved */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="include-resolved"
            checked={filters.includeResolved}
            onCheckedChange={(checked) => handleFilterChange('includeResolved', checked)}
            className="border-[rgb(39,39,42)]"
            data-testid="include-resolved-checkbox"
          />
          <label
            htmlFor="include-resolved"
            className="text-sm font-mono text-[rgb(161,161,170)] cursor-pointer"
          >
            Includi Risolte/Chiuse
          </label>
        </div>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="text-[rgb(161,161,170)] hover:text-white font-mono"
            data-testid="clear-filters"
          >
            <X className="h-4 w-4 mr-1" />
            Rimuovi Filtri
          </Button>
        )}

        <div className="flex-1" />

        {onRefresh && (
          <Button
            onClick={onRefresh}
            disabled={isRefreshing}
            variant="outline"
            className="border-[rgb(39,39,42)] hover:bg-white/5 font-mono"
            data-testid="refresh-filters"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Aggiornamento...' : 'Aggiorna'}
          </Button>
        )}

        {onAIAnalysis && (
          <Button
            onClick={onAIAnalysis}
            className="bg-[rgb(59,130,246)] hover:bg-[rgb(59,130,246)]/90 font-mono glow-primary"
            data-testid="open-ai-analysis"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Analisi AI
          </Button>
        )}
      </div>
    </div>
  );
}

export default FilterBar;
